/**
 * vizExecutor.js - Client-side visualization code executor
 *
 * executeVizCode(code, data, params) → { figure, error, logs }
 *
 * The user-provided code:
 *  - Has access to `viewData` (map of view name → row array)
 *  - Has access to `params` (visualization parameters)
 *  - Should return a Plotly figure: { data: [...], layout: {...} }
 */

export async function executeVizCode(code, data, params) {
  const logs = [];

  const patchedConsole = {
    log: (...args) => logs.push(args.map(a => (typeof a === 'object' ? JSON.stringify(a) : String(a))).join(' ')),
    warn: (...args) => logs.push('[warn] ' + args.map(a => (typeof a === 'object' ? JSON.stringify(a) : String(a))).join(' ')),
    error: (...args) => logs.push('[error] ' + args.map(a => (typeof a === 'object' ? JSON.stringify(a) : String(a))).join(' ')),
  };

  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function('viewData', 'params', 'console', `"use strict";\n${code}`);
    const result = fn(data || {}, params || {}, patchedConsole);
    const figure = result && typeof result.then === 'function' ? await result : result;

    if (!figure || !Array.isArray(figure.data)) {
      return {
        figure: null,
        error: { message: 'Visualization code must return { data: [...], layout: {...} }', line: null },
        logs,
      };
    }

    return { figure, error: null, logs };
  } catch (err) {
    let line = null;
    const match = err.stack?.match(/<anonymous>:(\d+)/);
    if (match) line = parseInt(match[1], 10) - 2; // subtract wrapper preamble line
    return { figure: null, error: { message: err.message, line }, logs };
  }
}
